import yaml from "js-yaml";

const baseAssetUrl = (import.meta.env?.BASE_URL ?? "/").replace(/\/*$/, "/");

const dataAssetUrls = Object.fromEntries(
  Object.entries(import.meta.glob("./data/**/*.yaml", { eager: true, import: "default", query: "?url" })).map(
    ([path, url]) => [path.replace("./data/", ""), url],
  ),
);

const dataMediaAssetUrls = Object.fromEntries(
  Object.entries(
    import.meta.glob("./data/**/*.{png,jpg,jpeg,webp,avif}", {
      eager: true,
      import: "default",
      query: "?url",
    }),
  ).map(([path, url]) => [path.replace("./data/", ""), url]),
);

const JOURNEY_MAIN_SUFFIX = "/main.yaml";

const normalizeResourceKey = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  let normalized = value.trim().replace(/\\/g, "/");
  if (!normalized) {
    return null;
  }
  while (normalized.startsWith("./")) {
    normalized = normalized.slice(2);
  }
  if (normalized.startsWith("/")) {
    normalized = normalized.slice(1);
  }
  if (normalized.startsWith("src/")) {
    normalized = normalized.slice(4);
  }
  if (normalized.startsWith("data/")) {
    normalized = normalized.slice(5);
  }
  return normalized;
};

const deriveJourneyKey = (key) => {
  if (key === "main.yaml") {
    return "main";
  }
  if (key === "levels.yaml") {
    return "levels";
  }
  if (key.endsWith(JOURNEY_MAIN_SUFFIX)) {
    return key.slice(0, -JOURNEY_MAIN_SUFFIX.length);
  }
  return null;
};

const journeyManifests = Object.keys(dataAssetUrls).reduce((acc, key) => {
  const journeyKey = deriveJourneyKey(key);
  if (journeyKey && !acc[journeyKey]) {
    acc[journeyKey] = key;
  }
  return acc;
}, {});

const toJourneySelection = (rootKey) => ({
  journeyKey: deriveJourneyKey(rootKey) ?? rootKey,
  rootKey,
});

const defaultJourneySelection = (() => {
  const preferredJourneyKeys = ["team_agentification_journey", "main", "levels"];
  for (const key of preferredJourneyKeys) {
    const rootKey = journeyManifests[key];
    if (rootKey) {
      return { journeyKey: key, rootKey };
    }
  }

  const firstManifest = Object.entries(journeyManifests)[0];
  if (firstManifest) {
    return { journeyKey: firstManifest[0], rootKey: firstManifest[1] };
  }

  const firstAssetKey = Object.keys(dataAssetUrls)[0];
  if (firstAssetKey) {
    return toJourneySelection(firstAssetKey);
  }

  return { journeyKey: null, rootKey: null };
})();

const envJourneyCandidates = [
  import.meta.env?.VITE_JOURNEY_KEY,
  import.meta.env?.VITE_JOURNEY_SOURCE,
  import.meta.env?.VITE_LEVELS_SOURCE,
].filter((value) => typeof value === "string" && value.trim() !== "");

const resolveJourneySelection = (rawValue) => {
  const normalized = normalizeResourceKey(rawValue);
  if (!normalized) {
    return null;
  }

  if (journeyManifests[normalized]) {
    return { journeyKey: normalized, rootKey: journeyManifests[normalized] };
  }

  const withoutMain = normalized.replace(/\/main\.yaml$/, "");
  if (journeyManifests[withoutMain]) {
    return { journeyKey: withoutMain, rootKey: journeyManifests[withoutMain] };
  }

  if (dataAssetUrls[normalized]) {
    return toJourneySelection(normalized);
  }

  return null;
};

const pickJourneySelection = () => {
  for (const candidate of envJourneyCandidates) {
    const resolved = resolveJourneySelection(candidate);
    if (resolved) {
      return resolved;
    }
  }
  return defaultJourneySelection;
};

let levelsCache = null;
let loadPromise = null;
let cachedJourneyKey = null;
const rawDocumentCache = new Map();

const getDirectory = (key) => {
  const normalized = normalizeResourceKey(key);
  if (!normalized) {
    return "";
  }
  const separatorIndex = normalized.lastIndexOf("/");
  return separatorIndex === -1 ? "" : normalized.slice(0, separatorIndex);
};

const resolveRelativeKey = (fromKey, target) => {
  if (!target) {
    return null;
  }
  const trimmed = target.trim().replace(/\\/g, "/");
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith("/")) {
    return normalizeResourceKey(trimmed);
  }

  const baseDir = getDirectory(fromKey);
  const stack = baseDir ? baseDir.split("/").filter(Boolean) : [];
  const segments = trimmed.split("/").filter(Boolean);

  if (!segments.length) {
    return normalizeResourceKey(trimmed);
  }

  segments.forEach((segment) => {
    if (segment === ".") {
      return;
    }
    if (segment === "..") {
      if (stack.length) {
        stack.pop();
      }
      return;
    }
    stack.push(segment);
  });

  if (!stack.length) {
    return normalizeResourceKey(trimmed);
  }

  return stack.join("/");
};

const resolveAssetKey = (value, fromKey = null, assetMap = dataAssetUrls) => {
  if (typeof value !== "string" || !assetMap) {
    return null;
  }

  const candidates = new Set();
  const normalized = normalizeResourceKey(value);
  if (normalized) {
    candidates.add(normalized);
  }

  if (fromKey) {
    const relative = resolveRelativeKey(fromKey, value);
    if (relative) {
      candidates.add(relative);
    }
    if (normalized && normalized !== value) {
      const relativeNormalized = resolveRelativeKey(fromKey, normalized);
      if (relativeNormalized) {
        candidates.add(relativeNormalized);
      }
    }
  }

  for (const candidate of candidates) {
    if (candidate && assetMap[candidate]) {
      return candidate;
    }
  }

  return null;
};

const resolveDataKey = (value, fromKey = null) => resolveAssetKey(value, fromKey, dataAssetUrls);

const resolveMediaKey = (value, fromKey = null) => resolveAssetKey(value, fromKey, dataMediaAssetUrls);

const fetchYamlDocument = async (resolvedKey) => {
  if (!resolvedKey) {
    throw new Error("Unable to resolve YAML document");
  }

  if (rawDocumentCache.has(resolvedKey)) {
    return rawDocumentCache.get(resolvedKey);
  }

  const url = dataAssetUrls[resolvedKey];
  if (!url) {
    throw new Error(`Unknown YAML asset: ${resolvedKey}`);
  }

  const pending = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load ${resolvedKey}: ${response.status}`);
      }
      return response.text();
    })
    .then((text) => {
      const parsed = yaml.load(text);
      if (!Array.isArray(parsed)) {
        throw new Error(`Parsed YAML (${resolvedKey}) is not an array`);
      }
      return parsed;
    })
    .catch((error) => {
      rawDocumentCache.delete(resolvedKey);
      throw error;
    });

  rawDocumentCache.set(resolvedKey, pending);
  return pending;
};

const hydrateBackgroundImageEntry = (rawValue, sourceKey) => {
  if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
    return null;
  }

  const hydrated = { ...rawValue };
  const fileValue = typeof hydrated.file === "string" ? hydrated.file : null;

  if (fileValue) {
    const resolvedFile = resolveMediaKey(fileValue, sourceKey) ?? normalizeResourceKey(fileValue);
    if (resolvedFile) {
      hydrated.file = resolvedFile;
      if (dataMediaAssetUrls[resolvedFile]) {
        hydrated.src = dataMediaAssetUrls[resolvedFile];
      }
    }
  }

  if (typeof hydrated.src !== "string" && typeof hydrated.file === "string") {
    const fallbackUrl = dataMediaAssetUrls[hydrated.file];
    if (fallbackUrl) {
      hydrated.src = fallbackUrl;
    }
  }

  return typeof hydrated.src === "string" ? hydrated : null;
};

const applyEntryAssets = (entry, sourceKey) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return entry;
  }

  if (entry.background_image && typeof entry.background_image === "object" && !Array.isArray(entry.background_image)) {
    const hydratedBackground = hydrateBackgroundImageEntry(entry.background_image, sourceKey);
    if (hydratedBackground) {
      entry.background_image = hydratedBackground;
    } else {
      delete entry.background_image;
    }
  }

  return entry;
};

const gatherEntriesFromDocument = async (key, options = {}) => {
  const { fromKey = null, seen = new Set() } = options;
  const resolvedKey = resolveDataKey(key, fromKey);

  if (!resolvedKey) {
    const context = fromKey ? ` from "${fromKey}"` : "";
    throw new Error(`Cannot resolve include "${key}"${context}`);
  }

  if (seen.has(resolvedKey)) {
    throw new Error(`Circular include detected for "${resolvedKey}"`);
  }

  seen.add(resolvedKey);

  try {
    const document = await fetchYamlDocument(resolvedKey);
    const collected = [];

    for (const entry of document) {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
        continue;
      }

      if (typeof entry.include !== "undefined") {
        const includeValue = entry.include;
        const includeItems = Array.isArray(includeValue) ? includeValue : [includeValue];

        for (const includePath of includeItems) {
          if (typeof includePath !== "string" || includePath.trim() === "") {
            continue;
          }
          const nested = await gatherEntriesFromDocument(includePath, { fromKey: resolvedKey, seen });
          collected.push(...nested);
        }
        continue;
      }

      applyEntryAssets(entry, resolvedKey);
      collected.push(entry);
    }

    return collected;
  } finally {
    seen.delete(resolvedKey);
  }
};

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

const mergeValues = (previous, next) => {
  if (typeof next === "undefined") {
    return previous;
  }

  if (Array.isArray(previous) && Array.isArray(next)) {
    return [...previous, ...next];
  }

  if (isPlainObject(previous) && isPlainObject(next)) {
    return Object.entries(next).reduce((acc, [key, value]) => {
      acc[key] = mergeValues(acc[key], value);
      return acc;
    }, { ...previous });
  }

  return next;
};

const mergeLevelEntry = (previous, next) => {
  if (!previous) {
    return { ...next };
  }

  return Object.entries(next).reduce((acc, [key, value]) => {
    if (key === "level" && typeof acc.level === "undefined") {
      acc.level = value;
      return acc;
    }
    acc[key] = mergeValues(acc[key], value);
    return acc;
  }, { ...previous });
};

const coerceArrayOfStrings = (value) =>
  Array.isArray(value)
    ? value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    : [];

const parseNumericLevel = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const coerceChallenges = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const problem = typeof item.problem === "string" ? item.problem.trim() : "";
      const solution = typeof item.solution === "string" ? item.solution.trim() : "";
      if (!problem && !solution) {
        return null;
      }
      return { problem, solution };
    })
    .filter(Boolean);
};

const coerceDimensions = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((acc, [key, raw]) => {
    const label = typeof key === "string" ? key : String(key ?? "");
    const text = typeof raw === "string" ? raw.trim() : "";
    if (label && text) {
      acc[label] = text;
    }
    return acc;
  }, {});
};

const mergeMeta = (previous, next) => {
  if (!next) {
    return previous ?? null;
  }

  if (!previous) {
    return next;
  }

  const result = { ...previous };

  Object.entries(next).forEach(([key, value]) => {
    if (key === "image_prompt") {
      result.image_prompt = Array.isArray(value) && value.length
        ? value
        : previous?.image_prompt ?? [];
      return;
    }

    if (typeof value !== "undefined") {
      result[key] = value;
    }
  });

  if (!Array.isArray(result.image_prompt) || !result.image_prompt.length) {
    result.image_prompt = previous?.image_prompt ?? [];
  }

  if (typeof result.title === "undefined") {
    result.title = previous?.title ?? null;
  }
  if (typeof result.rhetoric === "undefined") {
    result.rhetoric = previous?.rhetoric ?? null;
  }

  return result;
};

const normalizeMeta = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  if (typeof entry.level !== "undefined") {
    return null;
  }

  // If entry has a "meta" key at top level, flatten its contents
  const sourceEntry = entry.meta && typeof entry.meta === "object" && !Array.isArray(entry.meta) ? entry.meta : entry;

  const title = typeof sourceEntry.title === "string" ? sourceEntry.title.trim() : undefined;
  const rhetoric = typeof sourceEntry.rhetoric === "string" ? sourceEntry.rhetoric.trim() : undefined;
  const levels = Array.isArray(sourceEntry.levels) ? sourceEntry.levels : undefined;
  const imagePromptRaw = sourceEntry.image_prompt;
  const imagePrompt = Array.isArray(imagePromptRaw)
    ? coerceArrayOfStrings(imagePromptRaw)
    : typeof imagePromptRaw === "string"
      ? coerceArrayOfStrings([imagePromptRaw])
      : [];

  const knownKeys = new Set(["title", "rhetoric", "levels", "image_prompt", "include"]);
  const extras = Object.entries(sourceEntry).reduce((acc, [key, value]) => {
    if (knownKeys.has(key) || typeof value === "undefined" || key === "level") {
      return acc;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== "") {
        acc[key] = trimmed;
      }
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

  const hasMeaningfulData =
    (typeof title === "string" && title.length > 0) ||
    (typeof rhetoric === "string" && rhetoric.length > 0) ||
    (Array.isArray(levels) && levels.length) ||
    (Array.isArray(imagePrompt) && imagePrompt.length) ||
    Object.keys(extras).length > 0;

  if (!hasMeaningfulData) {
    return null;
  }

  const result = { ...extras };

  if (typeof title === "string" && title.length > 0) {
    result.title = title;
  }
  if (typeof rhetoric === "string" && rhetoric.length > 0) {
    result.rhetoric = rhetoric;
  }
  if (typeof levels !== "undefined") {
    result.levels = levels;
  }
  if (imagePrompt.length) {
    result.image_prompt = imagePrompt;
  }

  return result;
};

const slugify = (value, fallback) => {
  if (typeof value !== "string" || value.trim() === "") {
    return fallback;
  }

  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .trim() || fallback;
};

const DEFAULT_BACKGROUND_INSET = 16;

const coerceFiniteNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const normalizeInsetSides = (value, fallback = DEFAULT_BACKGROUND_INSET) => {
  const safeFallback = Math.max(0, fallback);
  const defaultSides = {
    top: safeFallback,
    right: safeFallback,
    bottom: safeFallback,
    left: safeFallback,
  };

  if (typeof value === "number" || typeof value === "string") {
    const parsed = coerceFiniteNumber(value);
    if (parsed !== null) {
      const clamped = Math.max(0, parsed);
      return { top: clamped, right: clamped, bottom: clamped, left: clamped };
    }
    return defaultSides;
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return defaultSides;
  }

  const top = coerceFiniteNumber(value.top ?? value.y ?? value.vertical);
  const right = coerceFiniteNumber(value.right ?? value.x ?? value.horizontal);
  const bottom = coerceFiniteNumber(value.bottom ?? value.y ?? value.vertical);
  const left = coerceFiniteNumber(value.left ?? value.x ?? value.horizontal);

  return {
    top: Math.max(0, top ?? safeFallback),
    right: Math.max(0, right ?? safeFallback),
    bottom: Math.max(0, bottom ?? safeFallback),
    left: Math.max(0, left ?? safeFallback),
  };
};

const normalizeQuadrantSlot = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  switch (normalized) {
    case "tl":
    case "top-left":
      return { row: 0, column: 0 };
    case "tr":
    case "top-right":
      return { row: 0, column: 1 };
    case "bl":
    case "bottom-left":
      return { row: 1, column: 0 };
    case "br":
    case "bottom-right":
      return { row: 1, column: 1 };
    default:
      return null;
  }
};

const normalizeBackgroundImage = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  const fileKey = typeof entry.file === "string" ? entry.file : null;
  const src = typeof entry.src === "string" ? entry.src : fileKey ? dataMediaAssetUrls[fileKey] : null;
  if (!src) {
    return null;
  }

  const layoutTypeRaw =
    typeof entry.layout === "string" && entry.layout.trim() !== ""
      ? entry.layout.trim().toLowerCase()
      : entry.quadrant
        ? "quadrant"
        : "";

  let layout = null;

  if (layoutTypeRaw === "quadrant") {
    const slot = normalizeQuadrantSlot(entry.quadrant);
    if (!slot) {
      return null;
    }
    layout = {
      type: "grid",
      rows: 2,
      columns: 2,
      row: slot.row,
      column: slot.column,
    };
  }

  if (!layout) {
    return null;
  }

  const insetSource =
    entry.inset ?? entry.padding ?? entry.border ?? entry.margin ?? entry.safe_inset ?? entry.safeInset;
  const inset = normalizeInsetSides(insetSource);

  return {
    src,
    file: fileKey,
    layout,
    inset,
  };
};

const normalizeLevel = (level, index) => {
  const numericLevel = parseNumericLevel(level.level);
  const fallbackLevelNumber = numericLevel ?? index + 1;
  const fallbackSlug = `level-${fallbackLevelNumber}`;
  const slug = level.slug || slugify(level.title, fallbackSlug);
  const colorTheme = typeof level.color_theme === "string" ? level.color_theme.trim() : "";
  const icon = typeof level.icon === "string" ? level.icon.trim() : "";
  const impact = typeof level.impact === "string" ? level.impact.trim() : "";
  const imagePrompt = typeof level.image_prompt === "string" ? level.image_prompt.trim() : "";
  const backgroundImage = normalizeBackgroundImage(level.background_image);

  return {
    ...level,
    slug,
    id: slug,
    level: numericLevel ?? index + 1,
    color_theme: colorTheme || null,
    icon: icon || null,
    impact: impact || null,
    image_prompt: imagePrompt || null,
    traits: Array.isArray(level.traits) ? level.traits : [],
    tools: Array.isArray(level.tools) ? level.tools : [],
    milestones: coerceArrayOfStrings(level.milestones),
    risks: coerceArrayOfStrings(level.risks),
    best_practices: coerceArrayOfStrings(level.best_practices),
    metrics: coerceArrayOfStrings(level.metrics),
    challenges: coerceChallenges(level.challenges),
    team_competencies: coerceDimensions(level.team_competencies || level.dimensions),
    thumbnail:
      typeof level.thumbnail === "string" && level.thumbnail.trim() !== ""
        ? level.thumbnail
        : `${baseAssetUrl}assets/generated/${slug}.png`,
    background_image: backgroundImage,
  };
};

const mergeEntries = (entries) => {
  const levelMap = new Map();
  let meta = null;

  entries.forEach((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return;
    }

    const maybeMeta = normalizeMeta(entry);
    if (maybeMeta) {
      meta = mergeMeta(meta, maybeMeta);
      return;
    }

    if (typeof entry.level === "undefined") {
      return;
    }

    const numericLevel = parseNumericLevel(entry.level);
    const key =
      numericLevel !== null
        ? String(numericLevel)
        : typeof entry.level === "string"
          ? entry.level.trim()
          : String(entry.level ?? "");

    const existing = levelMap.get(key);
    const merged = mergeLevelEntry(existing, entry);
    if (numericLevel !== null) {
      merged.level = numericLevel;
    }
    levelMap.set(key, merged);
  });

  const mergedLevels = Array.from(levelMap.values());
  mergedLevels.sort((a, b) => {
    const aLevel = Number.isFinite(a.level) ? a.level : 0;
    const bLevel = Number.isFinite(b.level) ? b.level : 0;
    return aLevel - bLevel;
  });

  const normalizedLevels = mergedLevels.map((level, index) => normalizeLevel(level, index));

  return { normalizedLevels, meta };
};

export async function loadLevels() {
  const selection = pickJourneySelection();

  if (!selection.rootKey) {
    throw new Error("No journey data available");
  }

  const cacheKey = selection.journeyKey ?? selection.rootKey;

  if (levelsCache && cachedJourneyKey === cacheKey) {
    return levelsCache;
  }

  if (!loadPromise) {
    const pendingCacheKey = cacheKey;
    const seen = new Set();

    loadPromise = gatherEntriesFromDocument(selection.rootKey, { seen })
      .then((entries) => {
        const { normalizedLevels, meta } = mergeEntries(entries);
        const result = {
          levels: normalizedLevels,
          meta,
          source: selection.journeyKey ?? selection.rootKey,
        };
        levelsCache = result;
        cachedJourneyKey = pendingCacheKey;
        return result;
      })
      .catch((error) => {
        loadPromise = null;
        throw error;
      })
      .finally(() => {
        loadPromise = null;
      });
  }

  return loadPromise;
}

export default loadLevels;
